import { useState, useEffect } from 'react'
import InputTimePicker from 'component/Input/InputTimePicker'
import { styled, Typography, Box, ClickAwayListener } from '@mui/material'
import { colors } from 'constants/theme'
import moment from 'moment'
import HelperText from './HelperText'

interface InputNewRangeTimePickerInterface {
  value: string[]
  onChange: (val: string[]) => void
  onBlurValidate?: (val: string[]) => boolean
  errMessage?: string
  disabled?: boolean
  suffixIcon?: any
  style?: any
  size?: 'small' | 'default'
  minmax?: any
  scopeTimeStart?: string
  scopeTimeEnd?: string
  disabledIntervals?: { timeStart: string, timeEnd: string }[]
}

const CustomRangeTimePicker = styled(Box)<{ size?: 'small' | 'default'; active?: boolean; error?: boolean; disabled?: boolean }>(({ theme, size, active, error, disabled }) => ({
  border: `${error ? '2px' : '1px'} solid ${(error && colors.statusInactive) || (active && colors.themeSecondColor) || colors.black12}`,
  backgroundColor: disabled ? colors.black12 : colors.white,
  borderRadius: 4,
  height: size === 'small' ? 32 : 43,
  '.range-time-picker': {
    '.has-text-field': {
      '.MuiInputBase-root': {
        height: size === 'small' ? 32 : 43,
        backgroundColor: colors.transparent,
        '.MuiOutlinedInput-input': {
          height: size === 'small' ? 32 : 43
        },
        '.MuiOutlinedInput-notchedOutline': {
          border: 'none'
        }
      },
      '.ant-picker': {
        height: size === 'small' ? 32 : 43,
        top: 0
      }
    },
    '&.first-range-picker': {
      '.ant-picker-suffix': {
        opacity: 0
      }
    },
    '&.last-range-picker': {
      '.has-text-field': {
        '.MuiInputBase-root': {
          '.MuiOutlinedInput-input': {
            paddingRight: '36px'
          }
        }
      }
    }
  }
}))

export default function InputNewRangeTimePicker(props: InputNewRangeTimePickerInterface) {
  const [firstTimePickerOpen, setFirstTimePickerOpen] = useState(false)
  const [lastTimePickerOpen, setLastTimePickerOpen] = useState(false)
  const [localStart, setLocalStart] = useState(props.value[0] || '')
  const [localEnd, setLocalEnd] = useState(props.value[1] || '')

  useEffect(() => {
    setLocalStart(props.value[0] || '')
    setLocalEnd(props.value[1] || '')
  }, [props.value[0], props.value[1]])

  useEffect(() => {
    firstTimePickerOpen && setLastTimePickerOpen(false)
  }, [firstTimePickerOpen])

  useEffect(() => {
    lastTimePickerOpen && setFirstTimePickerOpen(false)
  }, [lastTimePickerOpen])

  const handleMinMax = (time: 'timeStart' | 'timeEnd') => {
    if (props.minmax) {
      const outerStart = props.scopeTimeStart || '00:00'
      const outerEnd = props.scopeTimeEnd || '23:59'
      if (time === "timeStart") {
        const data = {
          scopeTimeStart: outerStart,
          scopeTimeEnd: localEnd && localEnd !== '' && moment(localEnd, 'HH:mm').format('HH:mm') !== "00:00" ? Math.abs(moment(localEnd, 'HH:mm').diff(moment(outerEnd, 'HH:mm'), 'minutes')) < 5 ? localEnd : moment(localEnd, 'HH:mm').subtract(5, 'minutes').format('HH:mm') : outerEnd
        }
        return data
      } else {
        const data = {
          scopeTimeStart: localStart && localStart !== '' && moment(localStart, 'HH:mm').format('HH:mm') !== "00:00" ? Math.abs(moment(localStart, 'HH:mm').diff(moment(outerStart, 'HH:mm'), 'minutes')) < 5 ? localStart : moment(localStart, 'HH:mm').add(5, 'minutes').format('HH:mm') : outerStart,
          scopeTimeEnd: outerEnd
        }
        return data
      }
    } else {
      return {}
    }

  }

  return (
    <ClickAwayListener
      onClickAway={() => {
        setFirstTimePickerOpen(false)
        setLastTimePickerOpen(false)
        if (localStart !== props.value[0] || localEnd !== props.value[1]) {
          if (props.onBlurValidate) {
            const isValid = props.onBlurValidate([localStart, localEnd])
            if (!isValid) {
              setLocalStart(props.value[0] || '')
              setLocalEnd(props.value[1] || '')
              return
            }
          }
          props.onChange([localStart, localEnd])
        }
      }}
    >
      <Box sx={{ ...props.style }}>
        <CustomRangeTimePicker disabled={props.disabled} error={props.errMessage ? true : false} active={firstTimePickerOpen || lastTimePickerOpen} size={props.size || 'default'} className={`d-flex align-items-center ${props.disabled && 'pe-none'}`}>
          <Box onClick={() => setFirstTimePickerOpen(true)} className="range-time-picker first-range-picker w-100">
            <InputTimePicker
              open={firstTimePickerOpen}
              inputTextAlign={'left'}
              value={localStart}
              onChange={(e: any, timeString: any, isAutoCorrected?: boolean) => {
                const newStart = moment(e).format('HH:mm')
                setLocalStart(newStart)
                if (!isAutoCorrected && moment(e?._i, 'HH:mm').format('mm') !== moment(e).format('mm') && (!localEnd || localEnd === "00:00")) {
                  setLastTimePickerOpen(true)
                }
                if (!props.onBlurValidate) {
                  props.onChange([newStart, localEnd])
                }
              }}
              disabledIntervals={props.disabledIntervals}
              pickerType="start"
              {...handleMinMax('timeStart')}
            />
          </Box>
          <Typography sx={{ fontSize: '16px' }}>⇀</Typography>
          <Box onClick={() => setLastTimePickerOpen(true)} className="range-time-picker last-range-picker w-100">
            <InputTimePicker
              open={lastTimePickerOpen}
              inputTextAlign={'left'}
              suffixIcon={props.suffixIcon}
              value={localEnd}
              onChange={(e: any) => {
                const newEnd = moment(e).format('HH:mm')
                setLocalEnd(newEnd)
                if (!props.onBlurValidate) {
                  props.onChange([localStart, newEnd])
                }
              }}
              disabledIntervals={props.disabledIntervals}
              pickerType="end"
              {...handleMinMax('timeEnd')}
            />
          </Box>
        </CustomRangeTimePicker>
        {props.errMessage && <HelperText label={props.errMessage} />}
      </Box>
    </ClickAwayListener>
  )
}
